import { useEffect, useRef, useState } from 'react';

// parity-audit-r2 (H2): restores the legacy Google Places autocomplete that the
// pre-migration profile/registration pages used (`include_google_maps` +
// GoogleLocationView). The Rails SPA shell injects the API token into
// `window.__GOOGLE_MAPS_API_TOKEN__` (PlatformWebController#show) when it is
// provisioned; we lazy-load the Places script here, per-page, only when a
// consuming component mounts — matching the legacy per-page `include_google_maps`
// rather than loading Maps globally.

declare global {
  interface Window {
    __GOOGLE_MAPS_API_TOKEN__?: string;
    __googleMapsPlacesReady__?: () => void;
    google?: any;
  }
}

export interface ParsedPlace {
  city: string;
  state: string;
  country: string;
  /** Recomposed "City, State, Country" string for the visible input. */
  formatted: string;
}

let scriptStatus: 'idle' | 'loading' | 'ready' | 'unavailable' = 'idle';
const readyCallbacks: (() => void)[] = [];

function placesAvailable(): boolean {
  return Boolean(window.google?.maps?.places?.Autocomplete);
}

function ensureScript() {
  if (scriptStatus === 'ready' || scriptStatus === 'unavailable') return;
  if (placesAvailable()) {
    scriptStatus = 'ready';
    return;
  }
  if (scriptStatus === 'loading') return;

  const token = window.__GOOGLE_MAPS_API_TOKEN__;
  if (!token) {
    // No token provisioned — geocode autocomplete is unavailable; consumers fall
    // back to parsing the free-text location. This is NOT an error.
    scriptStatus = 'unavailable';
    return;
  }

  scriptStatus = 'loading';
  window.__googleMapsPlacesReady__ = () => {
    scriptStatus = placesAvailable() ? 'ready' : 'unavailable';
    readyCallbacks.forEach((cb) => cb());
    readyCallbacks.length = 0;
  };

  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
    token,
  )}&libraries=places&callback=__googleMapsPlacesReady__`;
  script.async = true;
  script.defer = true;
  script.onerror = () => {
    scriptStatus = 'unavailable';
    readyCallbacks.forEach((cb) => cb());
    readyCallbacks.length = 0;
  };
  document.head.appendChild(script);
}

/**
 * Parse a Google Places result into discrete city/state/country, mirroring the
 * legacy GoogleLocationView `place_changed` handler (locality →city,
 * administrative_area_level_1 short_name →state, country short_name →country).
 */
export function parsePlace(place: any): ParsedPlace {
  let city = '';
  let state = '';
  let country = '';
  const components: any[] = place?.address_components ?? [];
  // parity-audit-r2 (Bugbot r3452497860): a Google address component carries
  // MULTIPLE types and the role we need (e.g. locality) is often not types[0].
  // Match against the whole types array. Fall back to postal_town / sublocality /
  // administrative_area_level_2 for "city" when no locality is present (common for
  // UK towns and some metros), mirroring how Places labels those regions.
  const has = (c: any, t: string) => Array.isArray(c.types) && c.types.includes(t);
  for (const c of components) {
    if (!city && has(c, 'locality')) city = c.long_name;
    if (has(c, 'administrative_area_level_1')) state = c.short_name;
    if (has(c, 'country')) country = c.short_name;
  }
  if (!city) {
    const fallback = components.find(
      (c) => has(c, 'postal_town') || has(c, 'sublocality') || has(c, 'administrative_area_level_2'),
    );
    if (fallback) city = fallback.long_name;
  }
  const formatted = [city, state, country].filter(Boolean).join(', ');
  return { city, state, country, formatted };
}

/**
 * Wire a text input to Google Places autocomplete. When the user picks a place,
 * `onPlace` fires with parsed city/state/country. Returns `available` =
 * whether geocode autocomplete is active (false → caller should parse the
 * free-text input itself so the value is never silently dropped).
 */
export function useGooglePlacesAutocomplete(
  inputRef: React.RefObject<HTMLInputElement>,
  onPlace: (place: ParsedPlace) => void,
): { available: boolean } {
  const [available, setAvailable] = useState<boolean>(false);
  const onPlaceRef = useRef(onPlace);
  onPlaceRef.current = onPlace;

  useEffect(() => {
    let cancelled = false;
    let autocomplete: any;
    let listener: any;

    const attach = () => {
      if (cancelled || !inputRef.current || !placesAvailable()) {
        if (scriptStatus === 'unavailable') setAvailable(false);
        return;
      }
      autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['(cities)'],
      });
      listener = autocomplete.addListener('place_changed', () => {
        onPlaceRef.current(parsePlace(autocomplete.getPlace()));
      });
      setAvailable(true);
    };

    ensureScript();
    if (scriptStatus === 'ready') {
      attach();
    } else if (scriptStatus === 'loading') {
      readyCallbacks.push(attach);
    } else {
      setAvailable(false);
    }

    return () => {
      cancelled = true;
      if (listener && window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [inputRef]);

  return { available };
}
